const DB_NAME = 'TheMurderDB'
const DB_VERSION = 1
const STORE_NAME = 'historias'

// Importar API para cargar archivos desde el servidor
import api from './api.js'

const KEYS = {
  HISTORIAS: 'historias',
  METODOS: 'metodos',
  REFERENCIAS: 'referencias',
  VINCULOS: 'vinculos',
  BOT_VINCULOS: 'bot_vinculos',
  CLASE_VINCULOS: 'clase_vinculos',
  OBJETOS: 'objetos',
  RASGOS_PERSONALIDAD: 'rasgos_personalidad',
  RASGOS_GENERO: 'rasgos_genero',
  RASGOS_EDAD: 'rasgos_edad',
  RASGOS_FISICOS: 'rasgos_fisicos',
  RASGOS_CLASE: 'rasgos_clase',
  RASGOS_ECONOMICOS: 'rasgos_economicos',
  CLASE_RASGOS_ECONOMICOS: 'clase_rasgos_economicos',
  AFINIDADES: 'afinidades',
  OCUPACIONES: 'ocupaciones',
  CLASE_OCUPACIONES: 'clase_ocupaciones',
  APELLIDOS: 'apellidos',
  EPILOGOS: 'epilogos',
  ESCENARIOS: 'escenarios',
  PERSONAJES: 'personajes',
  COLECCIONES: 'colecciones',
  DESCARTES_TIPOS_METODOS: 'descartes_tipos_metodos',
  TEXTOS_CONJUNCIONES_ADVERSATIVAS: 'textos_conjunciones_adversativas',
  TEXTOS_COINCIDENCIAS: 'textos_coincidencias'
}

let db = null
let syncChannel = null

// Inicializar canal de broadcast para sincronización entre pestañas
function initSyncChannel() {
  if (typeof BroadcastChannel !== 'undefined' && !syncChannel) {
    syncChannel = new BroadcastChannel('themurder-indexeddb-sync')
    console.log('📡 Canal de sincronización entre pestañas inicializado')
  }
}

function initDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => reject(request.error)

    request.onsuccess = () => {
      db = request.result
      resolve(db)
    }

    request.onupgradeneeded = (event) => {
      const db = event.target.result

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }
  })
}

async function saveToIndexedDB(key, value) {
  if (!db) await initDB()
  if (!syncChannel) initSyncChannel()

  return new Promise((resolve, reject) => {
    // Convertir Proxy/Ref a objeto plano para IndexedDB
    const plainValue = JSON.parse(JSON.stringify(value))
    
    const transaction = db.transaction([STORE_NAME], 'readwrite')
    const store = transaction.objectStore(STORE_NAME)
    const request = store.put(plainValue, key)

    request.onsuccess = () => {
      // Notificar a otras pestañas que este key fue actualizado
      if (syncChannel) {
        syncChannel.postMessage({ type: 'UPDATE', key })
        console.log(`📤 Notificando cambio en ${key} a otras pestañas`)
      }
      resolve()
    }
    request.onerror = () => reject(request.error)
  })
}

async function getFromIndexedDB(key) {
  if (!db) await initDB()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly')
    const store = transaction.objectStore(STORE_NAME)
    const request = store.get(key)

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function clearAllData() {
  if (!db) await initDB()

  const transaction = db.transaction([STORE_NAME], 'readwrite')
  const store = transaction.objectStore(STORE_NAME)

  const deletePromises = Object.values(KEYS).map(key =>
    new Promise((resolve) => {
      const request = store.delete(key)
      request.onsuccess = () => resolve()
      request.onerror = () => resolve() // Resolve even on error
    })
  )

  await Promise.all(deletePromises)
}

async function loadFromAssets(filename) {
  try {
    // Usar la API para cargar assets desde el servidor
    return await api.loadAsset(filename)
  } catch (error) {
    console.error(`Error loading ${filename}:`, error)
    return null
  }
}

async function loadInitialData() {
  const dataMap = {
    [KEYS.HISTORIAS]: { filename: 'historias.json', defaultValue: { historias: [] } },
    [KEYS.METODOS]: { filename: 'metodos.json', defaultValue: { metodos: [] } },
    [KEYS.REFERENCIAS]: { filename: 'referencias.json', defaultValue: { referencias: [] } },
    [KEYS.VINCULOS]: { filename: 'vinculos.json', defaultValue: { vinculos: [] } },
    [KEYS.BOT_VINCULOS]: { filename: 'bot_vinculos.json', defaultValue: { sabes_algo_mas: {}, bot_vinculos: [] } },
    [KEYS.CLASE_VINCULOS]: { filename: 'clase_vinculos.json', defaultValue: { clase_vinculos: [] } },
    [KEYS.OBJETOS]: { filename: 'objetos.json', defaultValue: { objetos: [] } },
    [KEYS.RASGOS_PERSONALIDAD]: { filename: 'rasgos_personalidad.json', defaultValue: { rasgos_personalidad: [] } },
    [KEYS.RASGOS_FISICOS]: { filename: 'rasgos_fisicos.json', defaultValue: { rasgos_fisicos: [] } },
    [KEYS.RASGOS_CLASE]: { filename: 'rasgos_clase.json', defaultValue: { rasgos_clase: [] } },
    [KEYS.RASGOS_ECONOMICOS]: { filename: 'rasgos_economicos.json', defaultValue: { rasgos_economicos: [] } },
    [KEYS.CLASE_RASGOS_ECONOMICOS]: { filename: 'clase_rasgos_economicos.json', defaultValue: { clase_rasgos_economicos: [] } },
    [KEYS.AFINIDADES]: { filename: 'afinidades.json', defaultValue: { afinidades: [] } },
    [KEYS.OCUPACIONES]: { filename: 'ocupaciones.json', defaultValue: { ocupaciones: [] } },
    [KEYS.CLASE_OCUPACIONES]: { filename: 'clase_ocupaciones.json', defaultValue: { clase_ocupaciones: [] } },
    [KEYS.APELLIDOS]: { filename: 'apellidos.json', defaultValue: { apellidos: [] } },
    [KEYS.EPILOGOS]: {
      filename: 'epilogos.json',
      defaultValue: {
        epilogos: {
          asesino: { hasganado: [], hasperdido: [] },
          inocente: {
            habeisganado: { hasacertado: [], hasfallado: [] },
            habeisperdido: { hasacertado: [], hasfallado: [] }
          }
        }
      }
    },
    [KEYS.ESCENARIOS]: { filename: 'escenarios.json', defaultValue: { escenarios: [] } },
    [KEYS.PERSONAJES]: { filename: 'personajes.json', defaultValue: { personajes: [] } },
    [KEYS.COLECCIONES]: { filename: 'colecciones.json', defaultValue: { colecciones: [] } }
    ,[KEYS.DESCARTES_TIPOS_METODOS]: { filename: 'descartes_tipos_metodos.json', defaultValue: { descartes_tipos_metodos: [] } }
        ,[KEYS.TEXTOS_CONJUNCIONES_ADVERSATIVAS]: { filename: 'textos_conjunciones_adversativas.json', defaultValue: { textos_conjunciones_adversativas: { conjunciones_adversativas: [] } } }
        ,[KEYS.TEXTOS_COINCIDENCIAS]: { filename: 'textos_coincidencias.json', defaultValue: { textos_coincidencias: { frases_coincidencia: [], coletillas_frases_coincidencia: [], frases_coincidencia_bot: [], coletillas_frases_coincidencia_bot: [] } } }
      }

  const result = {}

  for (const [key, { filename, defaultValue }] of Object.entries(dataMap)) {
    // First check if data exists in IndexedDB
    let data = await getFromIndexedDB(key)

    // If not in IndexedDB, try to load from assets
    if (!data) {
      data = await loadFromAssets(filename)

      // If loaded from assets, save to IndexedDB
      if (data) {
        await saveToIndexedDB(key, data)
      } else {
        // If not found in assets, use default value
        data = defaultValue
      }
    }

    result[key] = data
  }

  return result
}

export {
  initDB,
  saveToIndexedDB,
  getFromIndexedDB,
  clearAllData,
  loadInitialData,
  initSyncChannel,
  syncChannel,
  KEYS,
  DB_NAME,
  DB_VERSION,
  STORE_NAME
}
